/* eslint-disable max-len */
import { Injectable, signal, Signal } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import * as moment from 'moment';
import { UtilityService } from 'app/core/services/utility_functions/utility_functions.service';
import { DataCollectorService } from 'app/core/services/data_collector_services/data_collector_service.service';
import { MarkerStatus } from './map.interface';
import _ from 'lodash';
import * as L from 'leaflet';
import { mapAssetStatus } from 'app/core/constants/constants';
import { TitleCasePipe } from '@angular/common';
import { environment as env } from '../../../environments/environment';


@Injectable({
  providedIn: 'root'
})
export class MapsService {

  tab = new BehaviorSubject<string>('EMPLOYEE');
  prevTab: string;
  sidebar = new BehaviorSubject<boolean>(true);
  markerStatus = new Subject<MarkerStatus>();
  employeeLocationData = new Subject<any[]>();
  assetLocationData = new Subject<any[]>();
  propertyLocationData = new Subject<any[]>();
  jobLocationData = new Subject<any>();
  organizationLocationData = new Subject<any[]>();
  customerLocationData = new Subject<any[]>();
  routeLocationData = new Subject<any>();
  previewUid = new Subject<any>();
  updateList = new Subject<any>();
  routeDetailChange = new Subject<any>();
  timeFormat: any;
  serviceTerritoryLocationData = new BehaviorSubject<any[]>([]);
  legendDrawerToggler = new Subject<any>();
  newJobDrawerToggler = new Subject<any>();
  mapCurrentLocation = new Subject<any>();
  openPreviewDetailsInDispatchBoard = new Subject<any>();
  mapResizeIndicator = new Subject<boolean>();
  mapFitBoundByGeoCords = new Subject<any>();
  showOnMap = new BehaviorSubject<any>({});
  previewRouteDetails: Signal<any> = signal(null);
  openTooltip = { jobUid: null, routeUid: null };
  initalMapCurrentLocation: any[];

  //Draw on map component
  routeJobLocationData = new Subject<any>();
  allRoutesLocationData = new Subject<any>();
  clearDrawLayer = new Subject<any>();
  get mapsAPi(): any{
    return {
      getTeamSummary: this._dataCollectorService.baseurl() + '/teams/summary',
      getUsers: this._dataCollectorService.baseurl() + '/location/users',
      getAllUsers: this._dataCollectorService.baseurl() + '/user/all',
      getOnlineCount: this._dataCollectorService.baseurl() + '/location/count',
      getLocation: this._dataCollectorService.baseurl() + '/location/all',
      getAssetCategory: this._dataCollectorService.baseurl() + '/assets/category',
      getAssets: this._dataCollectorService.baseurl() + '/assets',
      getProperty: this._dataCollectorService.baseurl() + '/property',
      getJobs: this._dataCollectorService.baseurl() + '/jobs',
      getOrganizations: this._dataCollectorService.baseurl() + '/organization',
      getCustomers: this._dataCollectorService.baseurl() + '/customers',
      getRoutes: this._dataCollectorService.baseurl() + '/routes',
      getRoutesCount: this._dataCollectorService.baseurl() + '/routes/count',
      getServiceTerritory: this._dataCollectorService.baseurl() + '/territory',
      getServiceDetails: this._dataCollectorService.baseurl() + '/territory',
      getUserLocation: this._dataCollectorService.baseurl() + '/location'
    };
  }
  isLegendBarOpen: boolean = false;
  satelliteUrl: string = `https://maps.hereapi.com/v3/base/mc/{z}/{x}/{y}/png8?style=satellite.day&apiKey=${env.hereMapKey}`;

  constructor(
    private _utilityService: UtilityService,
    private _dataCollectorService: DataCollectorService,
    private titleCasePipe: TitleCasePipe
  ) {
    this.timeFormat = this._utilityService.timeFormat;
  }

  getCircleBounds(latitude: number, longitude: number, radius: number): any {
    const center = L.latLng(latitude, longitude);
    const latRadius = (radius / 111320);
    const lngRadius = (radius / (40075017 * Math.cos(center.lat * Math.PI / 180) / 360));
    const southWest = L.latLng(center.lat - latRadius, center.lng - lngRadius);
    const northEast = L.latLng(center.lat + latRadius, center.lng + lngRadius);
    return L.latLngBounds(southWest, northEast);
  }

  getOptimiseUrl(uid: any): string {
    return this.mapsAPi.getRoutes+'/'+uid+'/optimize';
  }

  addJobsUrl(uid: string): string {
    return this.mapsAPi.getRoutes+'/'+uid+'/job';
  }

  getUnAssignUrl(uid: string): string {
    return this.mapsAPi.getRoutes+'/'+uid+'/unassign';
  }

  getAssignUrl(uid: string): string {
    return this.mapsAPi.getRoutes+'/'+uid+'/assign';
  }

  getRouteAssignUrl(uid: string): string {
    return this.mapsAPi.getRoutes+'/'+uid+'/reassign';
  }

  getEmployeeIcon(data: any, rounded: boolean = false): string {
    return `
    <span class="border-2 border-white avatar-pin mt-2 ${rounded ? 'w-11 h-11 rounded-full' : 'w-14 h-13 rounded-lg'} block">
      <img alt="User avatar" 
      class="${rounded ? 'w-10 h-10 rounded-full' : 'w-14 h-13 rounded-md'} object-cover" 
      src=${data.profile_picture}>
    </span>
    `;
  }

  getCustomColorProfileIcon(eventDetails: any): any {
    if (!eventDetails?.color_profile?.color || !eventDetails?.color_profile?.icon) { return null; };
    const textColor = this._utilityService.getTextColorBasedOnBg(eventDetails?.color_profile?.color);
    const isBlackColor = textColor === '#000000';
    return L.divIcon({
      className: 'color-profile-icon',
      html: `<div class="rounded-full relative w-10 h-10 p-2 border-2 flex items-center justify-center ${isBlackColor? 'custom-icon-pin-black': 'custom-icon-pin-white'}" style="background: ${eventDetails?.color_profile?.color}; border-color: ${textColor}">
        <i class="ti ${eventDetails?.color_profile?.icon} text-3xl" style="color: ${textColor}"></i>  
      </div>`,
      iconAnchor: [15, 42]
    });
  }

  deleteJobUrl(uid: string): string {
    return this.mapsAPi.getRoutes+'/'+uid+'/job';
  }

  getDefaultJobIcon(): string {
    return `
      <div class="deafult-avatar-pin w-9 h-9 text-white rounded-full ring-2 ring-white flex justify-center items-center" style="background: #aa3685">
          <i class="ti ti-briefcase text-2xl"></i>
      </div>
    `;
  }

  getDefaultCustomerIcon(): string {
    return `
      <div class="deafult-avatar-pin w-9 h-9 text-white rounded-full ring-2 ring-white flex justify-center items-center" style="background: #704385">
          <i class="ti ti-users text-2xl"></i>
      </div>
    `;
  }

  getDefaultPropertyIcon(): string {
    return `
      <div class="deafult-avatar-pin w-9 h-9 text-white rounded-full ring-2 ring-white flex justify-center items-center" style="background: #b64a28">
          <i class="ti ti-buildings text-2xl"></i>
      </div>
    `;
  }


  getDefaultAssetIcon(): string {
    return `
      <div class="deafult-avatar-pin w-9 h-9 text-white rounded-full ring-2 ring-white flex justify-center items-center" style="background: #596bcd">
          <i class="ti ti-cube text-2xl"></i>
      </div>
    `;
  }

  getHomeIcon(color: string): string {
    return `
    <span class="w-10 h-10 border-2 border-white home-pin mt-2 flex items-center justify-center rounded-lg" style="background: ${color}">
      <i class="ti ti-home-2 text-white text-3xl"></i>
    </span>
    `;
  }

  getHomeAssignedToIcon(color: string, assignedTo: any[], totalJobs: number = 0): string {
    const textColor = this._utilityService.getTextColorBasedOnBg(color);
    const isBlackColor = textColor === '#000000';
    if (assignedTo?.length) {
      const maxAssigedToprofile = 4;
      let templateHTML = `<div class="border-2 p-2 rounded-xl w-fit ${isBlackColor? 'move-home-pin-black': 'move-home-pin'}" style="background: ${color}; border-color: ${textColor}">`;
      if (assignedTo?.length) {
        templateHTML += '<div class=\'flex items-center\' class=\'mt-2\'>';
        assignedTo?.slice(0, maxAssigedToprofile).forEach((assignee: any, index: number) => {
          const profilePicture = assignee?.user?.profile_picture;
          const userName = this._utilityService.concatTwoStrings(assignee?.user?.first_name, assignee?.user?.last_name) || assignee?.team?.team_name || '';
          if (profilePicture && userName && this._utilityService.hasValidProfilePicture(profilePicture)) {
            templateHTML += `<img class="h-6 w-6 relative block rounded-full ring-2 ring-white" loading="lazy" src="${profilePicture}" alt="${userName}" title="${userName}" style="margin-left: ${index === 0 ? 0 : (-12 + (2 * index))}px; z-index: ${maxAssigedToprofile - index};">`;
          }
          if (userName && !this._utilityService.hasValidProfilePicture(profilePicture)) {
            templateHTML += `<div title="${userName}" class="relative" style="margin-left: ${index === 0 ? 0 : (-12 + (2 * index))}px; z-index: ${maxAssigedToprofile - index};">
            <div class="flex items-center justify-center h-6 w-6 ring-2 ring-white rounded-full uppercase bg-gray-200 text-gray-600 font-medium dark:bg-gray-700 dark:text-gray-200"> ${userName.slice(0, 1)}</div>
          </div>`;
          }
        });
        if (assignedTo?.length > maxAssigedToprofile) {
          templateHTML += `<div title="+ ${assignedTo?.length - maxAssigedToprofile} More" class="relative" style="margin-left: ${-12 + (2 * maxAssigedToprofile + 1)}px; z-index: 0;">
          <div class="flex items-center justify-center h-6 w-6 ring-2 ring-white rounded-full uppercase bg-gray-200 text-gray-600 font-medium dark:bg-gray-700 dark:text-gray-200"> +${assignedTo?.length - maxAssigedToprofile}</div>
        </div>`;
        }
        templateHTML += `<span class='primary-font relative text-base ml-2 min-w-fit' style="margin-left: 6px; text-wrap: nowrap; color: ${textColor}"> | ${totalJobs}</span>`;
        templateHTML += '</div>';
      }
      templateHTML += '</div>';
      return templateHTML;
    }
    else {
      return `
      <span class="w-fit flex items-center border-2 p-2.5 justify-center rounded-lg  ${isBlackColor? 'move-home-pin-black': 'move-home-pin'}" style="background: ${color}; border-color: ${textColor}">
        <i class="ti ti-home-2 text-2xl" style="color: ${textColor}"></i>
        <span class='primary-font relative text-base ml-2 min-w-fit' style="margin-left: 4px; text-wrap: nowrap; color: ${textColor}"> | ${totalJobs}</span>
      </span>
      `;
    }
  }

  getRouteHomeIcon(color: string): string {
    const textColor = this._utilityService.getTextColorBasedOnBg(color);
    return `
    <span class="w-10 h-10 border-2  home-pin-black mt-2 flex justify-center rounded-full items-center" style="background: ${color}; border-color: ${textColor}">
      <i class="ti ti-home-filled ml-6 text-3xl" style='color: ${textColor}'></i>
    </span>
    `;
  }

  getRouteDestinationIcon(color: string): string {
    const textColor = this._utilityService.getTextColorBasedOnBg(color);
    return `
    <span class="w-10 h-10 border-2 home-pin-black mt-2 flex justify-center rounded-full items-center" style="background: ${color}; border-color: ${textColor}">
      <i class="ti ti-flag-filled text-3xl" style='color: ${textColor}'></i>
    </span>
    `;
  }

  getRouteJobIcon(color: string,index: number): string {
    const textColor = this._utilityService.getTextColorBasedOnBg(color);
    const isBlackColor = textColor === '#000000';
    return `
    <span class="w-10 h-10 border-2 mt-2 flex justify-center pt-1 rounded-full items-center text-xl font-semibold ${isBlackColor? 'move-home-pin-black': 'move-home-pin'}" style="background: ${color}; color: ${textColor}; border-color: ${textColor}">
      ${index}
    </span>
    `;
  }

  getDestinationIcon(color: string): string {
    const textColor = this._utilityService.getTextColorBasedOnBg(color);
    const isBlackColor = textColor === '#000000';
    return `
    <span class="w-10 h-10 border-2 mt-2 flex justify-center rounded-lg items-center ${isBlackColor? 'destination-pin-black': 'destination-pin'}" style="background: ${color}; border-color: ${textColor}">
      <i class="ti ti-player-stop text-3xl" style='color: ${textColor}'></i>
    </span>
    `;
  }

  getEmployeePopup(data: any): string {
    return `
    <div class="w-84 h-auto">
      <div class="flex h-11">
        <div class="w-3/5 pl-3">
          <div class="flex">
            <h4 class="max-w-36 truncate overflow-hidden text-lg font-bold">${data?.user?.first_name + ' ' + data?.user?.last_name}</h4>
            ${(data?.is_offline) ? '<span class="ml-2 mt-0.5 offline_status"> Offline</span>' : '<span class="ml-2 mt-0.5 online_status"> Online</span>'}
          </div>
          ${(data?.user?.designation) ? `<span class="text-sm text-gray-500">${data?.user?.designation}</span>` : ''}
        </div>
        <div class=" w-2/5 text-right pr-3">
          <p class="m-0 mt-4">Last seen at: ${moment(data?.track_time).format(this.timeFormat)}</p>
        </div>
      </div>
      <div class="flex h-11">
        <p class="w-1/2 flex justify-center items-center text-blue-700 border m-0 border-l-0 cursor-pointer ">Assign Job</p>
        <p class="w-1/2 flex justify-center items-center text-blue-700 border border-l-0 border-r-0 m-0 cursor-pointer ">Create Job</p>
      </div>
      <div class="bg-white w-full h-auto py-2 flex justify-around rounded-lg text-md">
        <div class="ml-4">
          <p class="text-center text-xl font-bold m-0">${data?.job_count}</p>
          <p class="my-1 text-center">${this._utilityService.getLocaleValue('text.jobs', 'Jobs')}</p>
        </div>
        <div class="ml-4">
          <p class="text-center text-xl font-bold m-0">${data?.battery}%</p>
          <p class="my-1 text-center">${this._utilityService.getLocaleValue('maps.employee_popup.battery', 'Battery')}</p>
        </div>
        <div class="ml-4">
          <p class="text-center text-xl font-bold m-0">${data?.signal_strength}%</p>
          <p class="my-1 text-center">${this._utilityService.getLocaleValue('maps.employee_popup.network', 'Network')}</p>
        </div>
        
      </div>
    </div>
    `;
  }

  getJobTitle(jobDetails: any): string {
    const jobTitle = [jobDetails?.prefix , jobDetails?.work_order_number, jobDetails?.job_title];
    return _.compact(jobTitle).join(' ');
  }

  toggleMapDrawTool(enable: boolean): void {
    const drawToolElementRef = document.querySelectorAll('.leaflet-top.leaflet-right');
    if (drawToolElementRef) {
      if(enable) {
        drawToolElementRef.forEach((elementRef: any) => {
          elementRef.classList.remove('hidden');
        });
      }
      else {
        drawToolElementRef.forEach((elementRef: any) => {
          elementRef.classList.add('hidden');
        });
        this.clearDrawLayer.next(true);
      }
    }
  }

  getDynamicRadius(zoom: number): number {
    if (zoom < 10) {
      return 20000;
    } else if (zoom < 15) {
      return 10000;
    } else {
      return 5000;
    }
  }

  setMapLegendActiveState(legendContainer: HTMLElement, isLegendBarOpen: boolean): void {
    if (!legendContainer) { return; }
    const panel = legendContainer.querySelector('.legend-btn');
    this.isLegendBarOpen = isLegendBarOpen;
    if (legendContainer) {
      if (isLegendBarOpen) {
        legendContainer.classList.add('open');
      } else {
        legendContainer.classList.remove('open');
      }
    }
    if (panel) {
      if (isLegendBarOpen) {
        panel.classList.add('open');
      } else {
        panel.classList.remove('open');
      }
    }
  }

  //Templates
  getCustomerPopup(data: any): string {
    const customerTitle = `${data?.customer_first_name}${data?.customer_last_name ? ' ' + data?.customer_last_name : ''}`;
    return `
      <div class="w-auto h-auto">
      <div class="flex justify-between mb-0 h-5 ml-3 mt-3">
        <h5 id="customer-title" class="text-base font-bold tracking-tight text-gray-900 cursor-pointer truncate w-11/12" title="${customerTitle}">${customerTitle}</h5>
      </div>
      <div class="flex my-1 ml-3" ${data?.customer_organization?.organization_name ? '' : 'style="display:none !important;"'}>
        <i class="ti ti-building text-base"></i>
        <span class="text-base ml-1">${data?.customer_organization?.organization_name}</span>
      </div>
       <div class="flex my-1 ml-3 pr-3" ${data?.customer_address ? '' : 'style="display:none !important;"'}>
        <i class="ti ti-map-pin text-base"></i>
        <span class="text-base ml-1">${data?.customer_address?.street}${data?.customer_address?.city ? ', ' + data?.customer_address?.city : ''}${data?.customer_address?.state ? ', ' + data?.customer_address?.state : ''}${data?.customer_address?.country ? ', ' + data?.customer_address?.country : ''}</span>
      </div> 
      <div class="flex h-11 mt-2">
        <a class="w-full flex justify-center items-center text-blue-700 border m-0 border-l-0 cursor-pointer hover:bg-gray-100 text-base" id='customer-create-job-btn'>${this._utilityService.getLocaleValue('create.job.text') }</a>
      </div>
      <div class="flex justify-between m-3">
        <span class="text-base online_status" ${data?.is_active ? '' : 'style="display:none !important;"'}> ${this._utilityService.getLocaleValue('active.text')}</span>
        <span class="text-base offline_status" ${!data?.is_active ? '' : 'style="display:none !important;"'}> ${this._utilityService.getLocaleValue('text.inactive')}</span>
        <span class="text-base">${this._utilityService.getLocaleValue('text.jobs')}: ${data?.no_of_jobs || 0}</span>
      </div>
    </div>`;
  }

  getAssetPopup(data: any): string {
    return `<div class="w-auto h-auto">
    <div class="block mb-0 h-5 ml-3 mt-3 w-11/12">
      <h5 id="asset-title" class="text-base font-bold tracking-tight text-gray-900 cursor-pointer truncate" title="${data?.asset_code ? data.asset_code + ' - ' : ''} ${data.asset_name}">${data?.asset_code ? data.asset_code + ' - ' : ''} ${data.asset_name}</h5>
    </div>
    <div class="flex my-1 ml-3" ${data?.asset_serial_number ? '' : 'style="display:none !important;"'}>
      <i class="ti ti-hash text-base"></i>
      <span class="text-base ml-1">${data?.asset_serial_number}</span>
    </div>
    <div class="flex my-1 ml-3" ${data?.organization?.organization_name ? '' : 'style="display:none !important;"'}>
      <i class="ti ti-building text-base"></i>
      <span class="text-base ml-1">${data?.organization?.organization_name}</span>
    </div>
    <div class="flex my-1 ml-3" ${data?.customer?.customer_first_name ? '' : 'style="display:none !important;"'}>
      <i class="ti ti-user text-base"></i>
      <span class="text-base ml-1">${data?.customer?.customer_first_name} ${data?.customer?.customer_last_name}</span>
    </div>
    <div class="flex my-1 ml-3 pr-3">
      <i class="ti ti-map-pin text-base"></i>
      <span class="text-base ml-1">${data?.asset_location?.street}${data?.asset_location?.city ? ', ' + data?.asset_location?.city : ''}${data?.asset_location?.state ? ', ' + data?.asset_location?.state : ''}${data?.asset_location?.country ? ', ' + data?.asset_location?.country : ''}</span>
    </div>
    <div class="flex justify-between m-3">
      <span class="text-base online_status" ${data?.is_active && !data?.asset_status ? '' : 'style="display:none !important;"'}> ${this._utilityService.getLocaleValue('active.text')}</span>
      <span class="text-base offline_status" ${!data?.is_active && !data?.asset_status ? '' : 'style="display:none !important;"'}> ${this._utilityService.getLocaleValue('text.inactive')}</span> 
      <span class="text-base status asset-status-${data?.asset_status}"> ${mapAssetStatus[data?.asset_status] || ''}</span>
      <span class="text-base">${data?.asset_category?.category_name}</span>
    </div>
  </div>`;
  }

  getPropertyPopup(data: any): string {
    return `
    <div class="w-auto h-auto">
      <div class="flex justify-between mb-0 h-5 ml-3 mt-3">
        <h5 id="property-title" class="text-base font-bold tracking-tight text-gray-900 cursor-pointer truncate w-11/12" title="${data.property_name}">${data.property_name}</h5>
      </div>
      <div class="flex my-1 ml-3" ${data?.property_organization?.organization_name ? '' : 'style="display:none !important;"'}>
        <i class="ti ti-building text-base"></i>
        <span class="text-base ml-1">${data?.property_organization?.organization_name}</span>
      </div>
      <div class="flex my-1 ml-3" ${data?.property_customers?.length > 0 ? '' : 'style="display:none !important;"'}>
        <i class="ti ti-user text-base"></i>
        <span class="text-base ml-1">${data?.property_customers?.[0]?.customer?.customer_first_name} ${data?.property_customers?.[0]?.customer?.customer_last_name}
        </span><a class="ml-1 text-indigo-500" ${data?.property_customers?.length > 1 ? '' : 'style="display:none !important;"'}>+${data?.property_customers?.length - 1} ${this._utilityService.getLocaleValue('text.more', 'More')?.toLowerCase()}</a>
       </div> 
       <div class="flex my-1 ml-3 pr-3" ${data?.property_address ? '' : 'style="display:none !important;"'}>
        <i class="ti ti-map-pin text-base"></i>
        <span class="text-base ml-1">${data?.property_address?.street}${data?.property_address?.city ? ', ' + data?.property_address?.city : ''}${data?.property_address?.state ? ', ' + data?.property_address?.state : ''}${data?.property_address?.country ? ', ' + data?.property_address?.country : ''}</span>
      </div> 
      <div class="flex h-11 mt-2">
        <a class="w-full flex justify-center items-center text-blue-700 border m-0 border-l-0 cursor-pointer hover:bg-gray-100 text-base" id='property-create-job-btn'>${this._utilityService.getLocaleValue('create.job.text')}</a>
      </div>
      <div class="flex justify-between m-3">
        <span class="text-base online_status" ${data?.is_active ? '' : 'style="display:none !important;"'}> ${this._utilityService.getLocaleValue('active.text', 'Active')}</span>
        <span class="text-base offline_status" ${!data?.is_active ? '' : 'style="display:none !important;"'}> ${this._utilityService.getLocaleValue('text.inactive', 'Inactive')}</span>
        <span class="text-base">${this._utilityService.getLocaleValue('text.jobs', 'Jobs')}: ${data?.no_of_jobs}</span>
      </div>
    </div>`;
  }

  getEmployeeLocationPopup(data: any): string {
    return `
    <div class="w-84 h-auto">
      <div class="flex h-11">
      <div class="w-3/5 pl-3">
        <div class="flex">
        <h4 class="max-w-36 truncate overflow-hidden text-lg font-bold cursor-pointer" id="user-title" title="${data?.first_name} ${data?.last_name}">${data?.first_name} ${data?.last_name}</h4>
        ${data?.location_data?.is_offline ? '<span class="ml-2 mt-0.5 offline_status">' + this._utilityService.getLocaleValue('text.inactive', 'Inactive') + '</span>' : '<span class="ml-2 mt-0.5 online_status">' + this._utilityService.getLocaleValue('active.text', 'Active') + '</span>'}
        </div>
        ${data?.designation ? `<span class="text-sm text-gray-500">${data?.designation}</span>` : ''}
      </div>
      <div class="w-2/5 text-right pr-3">
        <p class="m-0 mt-4">${this._utilityService.getLocaleValue('text.last_seen_at', 'Last seen at')}: ${moment(data?.location_data?.track_time).format(this.timeFormat)}</p>
      </div>
      </div>
      ${!data?.hideCreateJob ? `
      <div class="flex h-11 mt-1">
      <a class="w-full flex justify-center items-center text-blue-700 text-base border border-l-0 border-r-0 m-0 cursor-pointer hover:bg-gray-100" id="user-create-job-btn">${this._utilityService.getLocaleValue('create.job.text', 'Create Job')}</a>
      </div>` : ''}
      <div class="bg-white w-full h-auto py-2 flex justify-around text-md ${data?.hideCreateJob ? 'border-t border-gray-200 rounded-none' : 'rounded-lg'}">
      <div class="ml-4">
        <p class="text-center text-xl font-bold m-0">${data?.location_data?.job_count}</p>
        <p class="my-1 text-center">${this._utilityService.getLocaleValue('text.jobs', 'Jobs')}</p>
      </div>
      <div class="ml-4">
        <p class="text-center text-xl font-bold m-0">${data?.location_data?.battery}%</p>
        <p class="my-1 text-center">${this._utilityService.getLocaleValue('maps.employee_popup.battery', 'Battery')}</p>
      </div>
      <div class="ml-4">
        <p class="text-center text-xl font-bold m-0">${data?.location_data?.signal_strength}%</p>
        <p class="my-1 text-center">${this._utilityService.getLocaleValue('maps.employee_popup.network', 'Network')}</p>
      </div>
      </div>
    </div>`;
  }

  getServiceTerritoryPopup(data: any): string {
    const teamNames = data?.teams?.map((team: any) => team.team_name).join(', ') || '';
    const radius = data?.territory_radius?.radius ? (data.territory_radius.radius / 1000).toPrecision(2) : '';
    const unitName = this._utilityService.companyConfig?.general?.distance_metric === 'MILES' ? this._utilityService.getLocaleValue('text.miles', 'Mile(s)') : this._utilityService.getLocaleValue('route.distance.metric.kilo_meters', 'Km(s)');
    return `
        <div class="flex flex-col p-2 pt-0 space-y-2 text-base text-gray-700">
          <div class="flex items-center space-x-1">
            <i class="ti ti-compass text-2xl"></i>
            <span class="font-medium">${data?.territory_name || ''}</span>
          </div>
          ${data?.territory_description ? `<div class="flex items-start space-x-1">
            <i class="ti ti-note text-2xl"></i>
            <span>${data?.territory_description?.replace(/\n/g, '<br>') || ''}</span>
          </div>`: ''}
          ${teamNames ? `<div class="flex items-start space-x-1">
            <i class="ti ti-users-group text-2xl"></i>
            <span>${teamNames || ''}</span>
          </div>`: ''}
          <div class="flex items-start space-x-1">
            <i class="ti ti-map-route text-2xl"></i>
            <span>${ this.titleCasePipe.transform(data?.territory_type || '')}</span>
            ${data?.territory_radius?.radius && data?.territory_type === 'RADIUS' ? ` <div class="flex items-start space-x-1">
              <span>:</span>
              <span>${radius} ${unitName}</span>
            </div>`: ''}
          </div>
        </div>`;
  };
}
